import type { ReactNode } from "react"

import checkpointAgentSrc from "@/assets/features/sessions/checkpoint-agent.png"
import checkpointAuthorSrc from "@/assets/features/sessions/checkpoint-author.png"

// Building blocks shared by the new homepage's claim-card illustrations. They
// follow the Sessions feature page: small product vignettes made of floating
// cards, 24px icon rows, mono code boxes and the checkpoint avatars, all on the
// site's own tokens so they hold up in dark mode. Illustrations are decorative:
// callers wrap them in an `aria-hidden` slot and nothing inside is interactive.

export const illustrationCardClassName = "floating-card-surface rounded-lg"

/** Column of rows that fills the 244px-tall claim slot and centres vertically. */
export function IllustrationFrame({
  children,
  className = "",
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div className={`flex w-full min-w-0 flex-col justify-center gap-2 font-sans ${className}`}>
      {children}
    </div>
  )
}

/** A white floating card, the unit the Sessions page builds its vignettes from. */
export function IllustrationCard({
  children,
  className = "",
}: {
  children: ReactNode
  className?: string
}) {
  return <div className={`min-w-0 ${illustrationCardClassName} ${className}`}>{children}</div>
}

/** 24px circular icon well, used at the start of a row (tool calls, steps, agents). */
export function IllustrationIconWell({
  children,
  className = "",
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <span
      className={`flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-full bg-base p-1 text-neutral-400 shadow-[0_0_0_1px_rgb(0_0_0/0.06)] dark:shadow-[0_0_0_1px_rgb(255_255_255/0.1)] ${className}`}
    >
      {children}
    </span>
  )
}

/** An icon well and muted text, like a tool call in a session; long text wraps under itself. */
export function IllustrationIconRow({
  icon,
  children,
  tone = "muted",
}: {
  icon: ReactNode
  children: ReactNode
  tone?: "muted" | "default"
}) {
  return (
    <div className="flex min-h-6 items-start gap-3">
      <IllustrationIconWell>{icon}</IllustrationIconWell>
      <p
        className={`min-w-0 flex-1 px-3 text-sm leading-6 ${
          tone === "default" ? "text-default" : "text-muted"
        }`}
      >
        {children}
      </p>
    </div>
  )
}

/** The checkpoint avatars from the Sessions page: the person or the agent. */
export function IllustrationAvatar({ kind }: { kind: "author" | "agent" }) {
  if (kind === "author") {
    return (
      <img
        src={checkpointAuthorSrc}
        alt=""
        width={24}
        height={24}
        className="size-6 shrink-0 rounded-full"
        loading="lazy"
        decoding="async"
      />
    )
  }
  return (
    <span className="size-6 shrink-0 overflow-hidden rounded-full bg-floating shadow-[0_0_0_1px_rgb(0_0_0/0.06)] dark:shadow-[0_0_0_1px_rgb(255_255_255/0.1)]">
      <img
        src={checkpointAgentSrc}
        alt=""
        width={24}
        height={24}
        className="size-full object-cover"
        loading="lazy"
        decoding="async"
      />
    </span>
  )
}

/** Mono text box for commands, paths and output. `lines` keeps each line on one row. */
export function IllustrationMono({
  lines,
  className = "",
}: {
  lines: ReactNode[]
  className?: string
}) {
  return (
    <div
      className={`overflow-hidden rounded-lg border border-subtle bg-base px-3 py-2 font-mono text-[12px] leading-5 tracking-[0.24px] whitespace-nowrap ${className}`}
    >
      {lines.map((line, index) => (
        <div key={index} className="text-default">
          {line}
        </div>
      ))}
    </div>
  )
}

const pillTones = {
  neutral:
    "bg-floating text-default shadow-[0_0_0_1px_rgb(0_0_0/0.06)] dark:shadow-[0_0_0_1px_rgb(255_255_255/0.1)]",
  accent: "bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300",
  success: "bg-lime-100 text-lime-900 dark:bg-lime-950 dark:text-lime-300",
  warning: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300",
} as const

/** Small status pill, matching the badges in the hero cards. */
export function IllustrationPill({
  tone = "neutral",
  children,
}: {
  tone?: keyof typeof pillTones
  children: ReactNode
}) {
  return (
    <span
      className={`flex h-5 shrink-0 items-center rounded-sm px-1.5 text-xs leading-4 font-medium whitespace-nowrap ${pillTones[tone]}`}
    >
      {children}
    </span>
  )
}

/** A thin horizontal meter; `value` is 0–100, `tone` picks the fill colour. */
export function IllustrationMeter({
  value,
  tone = "accent",
  className = "",
}: {
  value: number
  tone?: "accent" | "success" | "warning" | "neutral"
  className?: string
}) {
  const fill = {
    accent: "bg-accent",
    success: "bg-[#7ccf00]",
    warning: "bg-[#fd9a00]",
    neutral: "bg-neutral-400",
  }[tone]
  return (
    <div className={`h-1.5 w-full overflow-hidden rounded-full bg-sunken ${className}`}>
      <div
        className={`h-full rounded-full ${fill}`}
        style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
      />
    </div>
  )
}
